import React, { ReactNode, Component } from 'react';
import './MeasurementsTable.scss';
import { ESortDirection, ETableColumnAlign, ResponsiveTable, ResponsiveTableColumn } from '../responsive-table/ResponsiveTable';
import { Search } from '../search/Search';
import { Measurement } from '../../models/Measurement';
import { DateHelper } from '../../helpers/DateHelper';
import { Icon } from '../icon/Icon';
import { RoundButton } from '../round-button/RoundButton';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import { AppNotificationType } from '../../models/AppNotification';
import { MeasurementTypeEnum } from '../../api/types';
import Badge from 'react-bootstrap/Badge';
import { connect } from 'react-redux';
import { ApplicationState } from '../../app/store';
import { IDossierV2 } from '../../models/DossierV2';
import { DossierMemberInterface } from '../../models/DossierMember';

interface IMeasurementsTableData {
  originalDataIndex: number;
  dateCreated: JSX.Element;
  dateAnswered: string;
  type: JSX.Element;
  question: string;
  answer: string;
  buttons: JSX.Element;
}

interface MeasurementsTableProps {
  dossier?: IDossierV2;
  dossierRelation?: DossierMemberInterface;
  measurements: Measurement[];
  onRepeatMeasurement: (measurement: Measurement) => void;
  onDeleteMeasurement: (measurement: Measurement) => void;
}

interface MeasurementsTableState {
  searchKeys?: string;
  filterDate?: Date;
  sortDirection: ESortDirection;
  sortedBy?: ResponsiveTableColumn<IMeasurementsTableData>;
  type?: MeasurementTypeEnum;
}

class MeasurementsTable extends Component<MeasurementsTableProps, MeasurementsTableState> {
  private columns: ResponsiveTableColumn<IMeasurementsTableData>[] = [];

  constructor(props: MeasurementsTableProps) {
    super(props);

    this.columns = [
      { title: 'Aangemaakt', objectKey: 'dateCreated', width: '8rem', customSortFunction: this.sort },
      { title: 'Type', objectKey: 'type', width: '3rem', customSortFunction: this.sort },
      { title: 'Stelling', objectKey: 'question', width: '8rem', customSortFunction: this.sort },
      { title: 'Beantwoord', objectKey: 'dateAnswered', width: '8rem', customSortFunction: this.sort },
      { title: 'Antwoord', objectKey: 'answer', width: '1rem', customSortFunction: this.sort },
    ];

    if (!props.dossier?.archived) {
      this.columns.push({ title: '', objectKey: 'buttons', width: '7rem', disableSort: true, hideBorder: true, horizontalAlign: ETableColumnAlign.END });
    }

    this.state = {
      sortDirection: ESortDirection.DESCENDING, // Default by Responsive table
    };
  }

  private datesAreOnTheSameDay = (a: Date, b: Date) => (
    a.getFullYear() === b.getFullYear() && a.getMonth === b.getMonth && a.getDate() === b.getDate()
  );

  filter = (row: IMeasurementsTableData): boolean => {
    const measurement = this.props.measurements[row.originalDataIndex];
    const { createAt } = measurement;
    const { filterDate } = this.state;
    if (filterDate && !this.datesAreOnTheSameDay(filterDate, createAt)) {
      return false;
    }
    const sanitizedKeys = this.state.searchKeys?.trim().toLowerCase();
    if (!sanitizedKeys || sanitizedKeys.length === 0) {
      return true;
    } else {
      const { question, typeHuman } = measurement;
      const inType = typeHuman ? typeHuman.toLowerCase().includes(sanitizedKeys) : false;
      const inQuestion = question ? question.toLowerCase().includes(sanitizedKeys) : false;
      return (inType || inQuestion);
    }
  };

  sort = (indexA: number, indexB: number, direction: ESortDirection, objectKey: keyof IMeasurementsTableData): number => {
    const a = this.props.measurements[indexA];
    const b = this.props.measurements[indexB];
    const sortDesc = direction === ESortDirection.DESCENDING;

    let result;
    switch (objectKey) {
      case 'dateCreated':
        result = sortDesc ? b.createAt.getTime() - a.createAt.getTime() : a.createAt.getTime() - b.createAt.getTime();
        break;
      case 'type':
        const aType = a.typeHuman ?? '0';
        const bType = b.typeHuman ?? '0';
        result = sortDesc ? aType.localeCompare(bType) : bType.localeCompare(aType);
        break;
      case 'question':
        const aQuestion = a.question ?? '0';
        const bQuestion = b.question ?? '0';
        result = sortDesc ? aQuestion.localeCompare(bQuestion) : bQuestion.localeCompare(aQuestion);
        break;
      case 'dateAnswered':
        const aAlt = a.answerAt ? a.answerAt : new Date();
        const bAlt = b.answerAt ? b.answerAt : new Date();
        result = sortDesc ? bAlt.getTime() - aAlt.getTime() : aAlt.getTime() - bAlt.getTime();
        break;
      case 'answer':
        const aAnswer = a.answer ?? 0;
        const bAnswer = b.answer ?? 0;
        result = sortDesc ? bAnswer - aAnswer : aAnswer - bAnswer;
        break;
      default:
        result = 0;
        break;
    }

    if (result === 0) {
      return b.createAt.getTime() - a.createAt.getTime(); // Fallback sort
    }
    return result;
  };

  handleSortChange = (direction: ESortDirection, by?: ResponsiveTableColumn<IMeasurementsTableData>): void => {
    this.setState({
      sortDirection: direction,
      sortedBy: by,
    });
  };

  handleSearch = (value: string): void => {
    this.setState({ searchKeys: value });
  };

  handleFilterDate = (measurement: Measurement): void => {
    this.setState({ filterDate: new Date(measurement.createAt) });
  };

  handleRemoveDateFilter = (): void => {
    this.setState({ filterDate: undefined });
  };

  handleRepeat = (measurement: Measurement): void => {
    this.props.onRepeatMeasurement(measurement);
  };

  handleDelete = (measurement: Measurement): void => {
    this.props.onDeleteMeasurement(measurement);
  };

  handleCopyResultsToClipboard = (displayData: IMeasurementsTableData[]): void => {
    const result: { [key: string]: string[] } = {};
    displayData.forEach(row => {
      const measurement = this.props.measurements[row.originalDataIndex];
      const key = `${measurement.typeHuman || ''}/${measurement.question}`;
      if (!result[key]) {
        result[key] = [];
      }
      if (measurement.answer !== null && measurement.answer !== undefined) {
        result[key].push(`${measurement.answer}`);
      } else {
        result[key].push('?');
      }
    });

    const sortColumn = this.state.sortedBy?.title ?? '[geen]';
    const sortDirection = this.state.sortDirection === ESortDirection.DESCENDING ? 'aflopend' : 'oplopend';
    let output = `Sortering resultaat: ${sortColumn} (${sortDirection})\n`;
    Object.keys(result).forEach(key => {
      output += `${key}: ${result[key].join(', ')}\n`;
    });

    if (navigator.clipboard) {
      navigator.clipboard.writeText(output)
        .then(() => this.showToast('Metingen naar klembord gekopieërd!'))
        .catch(() => this.showErrorToast('Kopiëren naar klembord mislukt door een onverwachte fout.'));
    } else {
      this.showErrorToast('Kopiëren naar klembord wordt niet ondersteund op dit apparaat.');
    }
  };

  private showToast(message: string): void {
    NotificationHelper.push(message, { type: AppNotificationType.SUCCESS, silent: true, alwaysToast: true });
  }

  private showErrorToast(message: string): void {
    NotificationHelper.push(message, { type: AppNotificationType.ERROR, silent: true, alwaysToast: true });
  }

  private typeToIcon(type: MeasurementTypeEnum, className?: string): JSX.Element {
    const fullClassName = className ? className : '';
    switch (type) {
      case MeasurementTypeEnum.CREDIBILITY:
        return <div className={fullClassName} title={Measurement.typeToHuman(type)}>
          <Icon icon="gauge" />
        </div>;
      case MeasurementTypeEnum.TENSION:
        return <div className={fullClassName} title={Measurement.typeToHuman(type)}>
          <Icon icon="thermometer" />
        </div>;
      default:
        return <div className={fullClassName} title={Measurement.typeToHuman(type)}>
          {Measurement.typeToHuman(type)}
        </div>;
    }
  }

  render(): ReactNode {
    const { filterDate } = this.state;
    const { measurements } = this.props;
    const { columns } = this;

    const data: IMeasurementsTableData[] = measurements.map((measurement, index) => {
      const { createAt, answerAt, type, question, answerSummery } = measurement;

      return {
        originalDataIndex: index,
        dateCreated: <>
          <div className="measurements-overview-item--created-at">
            <button
              onClick={() => this.handleFilterDate(measurement)}
              className="measurements-overview__filter-button"
              title="Filter meting op deze datum"
            >
              <Icon icon="filter" />
            </button>
            {DateHelper.toDateTimeString(createAt)}
          </div>
        </>,
        type: this.typeToIcon(type),
        question: question,
        dateAnswered: answerAt ? DateHelper.toDateTimeString(answerAt) : '',
        answer: answerSummery,
        buttons: (
          <>
            { <RoundButton label="Herhalen" type="repeat" onClick={() => this.handleRepeat(measurement)} /> }
            { <RoundButton label="Verwijder" type="delete" onClick={() => this.handleDelete(measurement)} /> }
          </>
        )
      };
    });
    let displayData = data;

    return (
      <>
        <div className="measurements-overview">
          <div className="sort-search-container">
            <Search onSearch={this.handleSearch} placeholder="Zoek op type of stelling"/>
          </div>
          <div className="measurements-overview__copy">
            <button
              onClick={() => this.handleCopyResultsToClipboard(displayData)}
              title="Kopiëren naar klembord"
              className="measurements-overview__copy-button"
            >
              <Icon icon="copy" />
              <label className="measurements-overview__copy-button-label">Resultaten kopiëren</label>
            </button>
          </div>
          <div className="measurements-overview__filter-pills">
            {
              filterDate &&
              <Badge pill onClick={this.handleRemoveDateFilter}>
                Gefilterd op datum <Icon icon="delete" />
              </Badge> }
          </div>
          <ResponsiveTable<IMeasurementsTableData>
            className="measurements-overview__table"
            columns={columns}
            data={data}
            sortByDefault={this.columns[0]}
            onSortChange={this.handleSortChange}
            onDisplayDataChange={data => (displayData = data) }
            filterFunction={this.filter}
          />
        </div>
      </>
    );
  }
}

export default connect((state: ApplicationState) => ({
  dossier: state.session.dossier,
  dossierRelation: state.session.relation,
}))(MeasurementsTable);
